import React, { useCallback, useEffect, useRef, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails, List, ListItem, Typography } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { monitorReason } from "../utils/monitorSnapshotState";
import { useTranslation } from "react-i18next";
import { api, isSuperAdmin } from "../utils/helpers";
import "./ErrorMonitorCosts.css";

const ENDPOINT = "/api/admin/monitoring/error-database";
const STATUSES = ["active", "budget_paused", "unavailable", "disabled"];
const amountKnown = value => Number.isFinite(value) && value >= 0;
const countKnown = value => Number.isSafeInteger(value) && value >= 0;
const timestamp = value => {
  if (typeof value !== "number" && (typeof value !== "string" || !/T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value))) return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
};

// The page owns the refresh interval. This hook only loads on mount and on request.
export function useErrorMonitorCosts(user) {
  const allowed = isSuperAdmin(user);
  const owner = allowed ? user.id : null;
  const [snapshot, setSnapshot] = useState({ owner: null, data: null, error: null, busy: false });
  const request = useRef(null);
  const refresh = useCallback(async () => {
    if (!allowed || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setSnapshot(previous => ({ ...(previous.owner === owner ? previous : { data: null, error: null }), owner, busy: true }));
    try {
      const data = await api(ENDPOINT, { signal: controller.signal });
      if (!data || Array.isArray(data) || typeof data.enabled !== "boolean" || typeof data.status !== "string" || data.currency !== "USD") {
        const error = new Error("INVALID_RESPONSE");
        error.code = "invalid";
        throw error;
      }
      if (!controller.signal.aborted) setSnapshot({ owner, data, error: null, busy: false });
    } catch (error) {
      if (!controller.signal.aborted) {
        const denied = error?.status === 401 || error?.status === 403;
        const code = denied ? "access" : error?.code === "invalid" ? "invalid" : "fetch";
        const httpStatus = Number.isInteger(error?.status) && error.status >= 400 && error.status <= 599 ? error.status : null;
        setSnapshot(previous => ({ owner, data: denied ? null : previous.data, error: { code, httpStatus }, busy: false }));
      }
    } finally {
      if (request.current === controller) request.current = null;
    }
  }, [allowed, owner]);

  useEffect(() => {
    setSnapshot({ owner, data: null, error: null, busy: allowed });
    refresh();
    return () => { request.current?.abort(); request.current = null; };
  }, [allowed, owner, refresh]);

  return {
    ...(allowed && snapshot.owner === owner ? snapshot : { data: null, error: null, busy: false }),
    refresh,
  };
}

export default function ErrorMonitorCosts({ data = null, error = null, busy = false }) {
  const { t, i18n } = useTranslation("errorMonitorCosts");
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
  const unknown = t("message.unknown");
  const money = value => amountKnown(value)
    ? new Intl.NumberFormat(locale, { style: "currency", currency: "USD", currencyDisplay: "code" }).format(value)
    : unknown;
  const number = value => countKnown(value) ? value.toLocaleString(locale) : unknown;
  const bytes = value => countKnown(value) ? t("metric.bytes", { value: number(value) }) : unknown;
  const date = value => {
    const parsed = timestamp(value);
    return parsed ? `${parsed.toLocaleString(locale, { timeZone: "UTC" })} UTC` : unknown;
  };
  const month = typeof data?.month === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(data.month)
    ? new Date(`${data.month}-01T00:00:00Z`).toLocaleDateString(locale, { month: "long", year: "numeric", timeZone: "UTC" })
    : unknown;
  const status = STATUSES.includes(data?.status) ? data.status : "unknown";
  const reasonCode = typeof data?.reason === "string" && /^[a-z][a-z0-9_-]{0,95}$/i.test(data.reason) ? data.reason : null;
  const reason = reasonCode ? t(`reason.${reasonCode}`, { code: reasonCode, defaultValue: t("reason.other", { code: reasonCode }) }) : t("reason.unknown");
  const pricing = data?.priceSource && typeof data.priceSource === "object" && !Array.isArray(data.priceSource) && Object.keys(data.priceSource).length ? data.priceSource : null;
  const priceSource = typeof data?.priceSource === "string" && data.priceSource.trim() ? data.priceSource : unknown;
  const tariff = value => amountKnown(value)
    ? new Intl.NumberFormat(locale, { style: "currency", currency: "USD", currencyDisplay: "code", maximumFractionDigits: 6 }).format(value) : unknown;
  const diagnostics = Array.isArray(data?.sourceDiagnostics) ? data.sourceDiagnostics.filter(source => source && typeof source.sourceId === "string" && source.sourceId.trim()) : null;
  const readStates = ["not_loaded", "loading", "available", "partial", "unavailable"];
  const collectionStates = ["active", "unavailable", "paused"];
  const retainedCount = data?.entries?.kind === "retained-error-records" ? data.entries.count : null;
  const billed = Number.isFinite(data?.billedUsd) && timestamp(data?.billedAsOf);
  const metric = (key, value, detail) => <div className={`error-costs-metric error-costs-metric--${key}`} key={key}>
    <dt>{t(`metric.${key}`)}</dt><dd>{value}</dd>{detail && <dd className="error-costs-detail">{detail}</dd>}
  </div>;

  return <section className="error-costs" aria-labelledby="error-monitor-costs-title">
    <header className="error-costs-header">
      <div><h2 id="error-monitor-costs-title">{t("heading.title")}</h2><p>{t("heading.scope")}</p></div>
      <p>{t("heading.month", { month })}</p>
    </header>
    <div className="error-costs-body" aria-busy={busy}>
      {error && <p role="alert" className="error-costs-notice">
        {t(`message.${error.code === "access" ? "access" : error.code === "invalid" ? "invalid" : error.httpStatus ? "http" : "fetch"}`, { status: error.httpStatus })}
        {data && <> {t("message.stale", { time: date(data.updatedAt) })}</>}
      </p>}
      {!data && busy && <p role="status">{t("message.loading")}</p>}
      <div className="error-costs-state">
        <p className={`error-costs-status error-costs-status--${error && data ? "unknown" : status}`}>
          {t(error && data ? "status.previous" : "status.label")} <strong>{t(`status.${status}`)}</strong>
        </p>
        <p>{t("heading.updated", { time: date(data?.updatedAt) })}</p>
      </div>
      {data && status !== "active" && <div className="error-costs-notice" role={status === "budget_paused" || status === "unavailable" ? "status" : undefined}>
        <p><strong>{t(status === "budget_paused" ? "pause.title" : `status.${status}`)}</strong></p>
        <p>{t("pause.reason", { reason })}</p>
        {status === "budget_paused" && <p>{t("pause.effect")}</p>}
      </div>}
      <dl className="error-costs-primary">
        {metric("estimated", money(data?.estimatedUsd), t("metric.estimatedHint"))}
        {metric("limit", money(data?.limitUsd))}
        {metric("entries", number(retainedCount), t("metric.entriesAsOf", { time: date(data?.entries?.asOf) }))}
      </dl>
      <dl className="error-costs-secondary">
        {metric("reserve", money(data?.reserveUsd))}
        {metric("threshold", money(data?.stopThresholdUsd))}
        {metric("reserved", money(data?.reservedUsd), t("metric.reservedHint"))}
        {metric("remaining", money(data?.remainingUsd))}
        {metric("retention", countKnown(data?.retentionDays) ? t("metric.days", { count: data.retentionDays }) : unknown)}
        {metric("storage", bytes(data?.storageBytes))}
        {metric("storageLimit", bytes(data?.maxStoreBytes), t("metric.storageLimitHint"))}
        {data?.storageBytesUpperBound != null && metric("storageUpperBound", bytes(data.storageBytesUpperBound), t("metric.storageUpperBoundHint"))}
        {metric("physicalEntries", number(data?.physicalItems))}
      </dl>
      {data?.recoveryMode === true && <p className="error-costs-notice" role="status">{t("reason.recovery_in_progress")}</p>}
      {diagnostics && <Accordion className="error-costs-diagnostics" disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreRounded/>} aria-controls="error-costs-source-details" id="error-costs-source-summary">
          <Typography component="span">{t("diagnostics.title", { count: diagnostics.length })}</Typography>
        </AccordionSummary>
        <AccordionDetails id="error-costs-source-details">
          {diagnostics.length === 0 ? <p>{t("diagnostics.empty")}</p> : <List disablePadding aria-label={t("diagnostics.sources")}>
            {diagnostics.map(source => <ListItem key={source.sourceId} disableGutters className="error-costs-source">
              <Typography component="h3" variant="subtitle1">{typeof source.name === "string" && source.name.trim() ? source.name : source.sourceId}</Typography>
              <dl>
                <div><dt>{t("diagnostics.collection")}</dt><dd>{t(`diagnostics.collectionState.${collectionStates.includes(source.collectionState) ? source.collectionState : "unknown"}`)}</dd></div>
                <div><dt>{t("diagnostics.read")}</dt><dd>{t(`diagnostics.readState.${readStates.includes(source.readState) ? source.readState : "unknown"}`)}</dd></div>
                <div><dt>{t("diagnostics.observed")}</dt><dd>{date(source.observedAt)}</dd></div>
                <div><dt>{t("diagnostics.checked")}</dt><dd>{date(source.checkedAt)}</dd></div>
                <div><dt>{t("diagnostics.loaded")}</dt><dd>{number(source.loadedRecords)}</dd></div>
                <div><dt>{t("diagnostics.gaps")}</dt><dd>{number(source.gapCount)}</dd></div>
              </dl>
              {(source.reason || source.collectionState !== "active") && <p>{t("pause.reason", { reason: monitorReason(source, t) })}</p>}
              {timestamp(source.nextRetryAt) && <p>{t("notice.nextRetry", { time: date(source.nextRetryAt) })}</p>}
            </ListItem>)}
          </List>}
        </AccordionDetails>
      </Accordion>}
      <p className="error-costs-retention">{t("explanation.retention")}</p>
      <p className="error-costs-reset">{t("pause.reset", { time: date(data?.nextResetAt) })}</p>
      <div className="error-costs-billing">
        <h3>{t("billing.title")}</h3>
        {billed ? <><p className="error-costs-billed">{new Intl.NumberFormat(locale, { style: "currency", currency: "USD", currencyDisplay: "code" }).format(data.billedUsd)}</p><p>{t("billing.asOf", { time: date(data.billedAsOf) })}</p></>
          : <p>{t(Number.isFinite(data?.billedUsd) ? "billing.missingTimestamp" : "billing.missing")}</p>}
      </div>
      <div className="error-costs-explanation">
        {pricing ? <details className="error-costs-pricing">
          <summary>{t("pricing.title")}</summary>
          <p>{t("pricing.asOf", { time: date(pricing.asOf) })}</p>
          <p>{t("pricing.validUntil", { time: date(pricing.validUntil) })}</p>
          <dl>
            <div><dt>{t("pricing.region")}</dt><dd>{typeof pricing.region === "string" && pricing.region ? pricing.region : unknown}</dd></div>
            {["readUsdPerMillion", "writeUsdPerMillion", "storageUsdPerGiBMonth", "pitrUsdPerGiBMonth"].map(key => <div key={key}><dt>{t(`pricing.${key}`)}</dt><dd>{tariff(pricing[key])}</dd></div>)}
            <div><dt>{t("pricing.buffer")}</dt><dd>{amountKnown(pricing.buffer) ? new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 2 }).format(pricing.buffer) : unknown}</dd></div>
          </dl>
          <p>{t("pricing.pitrHint")}</p>
        </details> : <p>{t("explanation.source", { source: priceSource })}</p>}
        <p>{t("explanation.budget")}</p>
        <p>{t("explanation.billing")}</p>
      </div>
    </div>
  </section>;
}
